import { useState, useEffect } from "react"
import { useParams, Link } from "react-router-dom"
import { Building2, MapPin, BedDouble, Bath, Edit, Plus, Loader2, UserPlus, Maximize2, UserMinus } from "lucide-react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { toast } from "sonner"

import { useProperty } from "@/features/properties/hooks/useProperties"
import { propertiesService } from "@/features/properties/services/propertiesService"
import { PropertyForm } from "@/features/properties/components/PropertyForm"
import { useLeases, useCreateLease, useUnassignLease } from "@/features/leases/hooks/useLeases"
import { useMaintenanceIssues } from "@/features/maintenance/hooks/useMaintenance"
import { useTenants } from "@/features/tenants/hooks/useTenants"
import { PageHeader } from "@/components/shared/PageHeader"
import { StatusBadge } from "@/components/shared/StatusBadge"
import { ListSkeleton } from "@/components/shared/LoadingSkeleton"
import { ImageGalleryModal } from "@/components/shared/ImageGalleryModal"
import { ConfirmDialog } from "@/components/shared/ConfirmDialog"
import { Card, CardContent } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Button } from "@/components/ui/button"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { APP_NAME } from "@/lib/appConfig"
import { CURRENCIES } from "@/lib/constants"
import { useCurrency } from "@/hooks/useCurrency"
import type { PropertyPhoto } from "@rentbase/shared"

function formatDate(dateStr?: string | null) {
  if (!dateStr) return "—"
  return new Date(dateStr).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  })
}

function PhotoGallery({ photos }: { photos: PropertyPhoto[] }) {
  const cover = photos.find((p) => p.is_cover) ?? photos[0]
  const [active, setActive] = useState<PropertyPhoto | null>(cover ?? null)
  const [isGalleryOpen, setIsGalleryOpen] = useState(false)
  const [galleryIndex, setGalleryIndex] = useState(0)

  if (!photos.length) return null

  const current = active ?? cover

  return (
    <div className="relative w-full h-[40vh] bg-muted mb-6">
      {current ? (
        <img src={current.url} alt="" className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <Building2 className="h-16 w-16 text-muted-foreground/30" />
        </div>
      )}

      {/* Abstract gradient overlay so text/cards can fade in smoothly */}
      <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent pointer-events-none" />

      {photos.length > 1 && (
        <div className="absolute bottom-6 left-4 right-4 flex gap-3 overflow-x-auto p-1 no-scrollbar z-10">
          {photos.map((photo, idx) => (
            <div key={photo.id} className="relative group shrink-0 w-16 h-16 rounded-2xl overflow-hidden border-2 transition-all duration-300 ease-out border-white/30 backdrop-blur-md">
              <button
                type="button"
                onClick={() => setActive(photo)}
                className={`w-full h-full ${current?.id === photo.id ? "border-primary scale-110 shadow-glow" : "opacity-70 hover:opacity-100 hover:scale-105"}`}
              >
                <img src={photo.url} alt="" className="w-full h-full object-cover" />
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  setGalleryIndex(idx)
                  setIsGalleryOpen(true)
                }}
                className="absolute top-1 right-1 bg-black/50 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/80"
                title="View Gallery"
              >
                <Maximize2 className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      <ImageGalleryModal
        images={photos.map(p => p.url)}
        initialIndex={galleryIndex}
        isOpen={isGalleryOpen}
        onClose={() => setIsGalleryOpen(false)}
      />
    </div>
  )
}

const assignSchema = z.object({
  tenant_id: z.string().min(1, "Tenant is required"),
  start_date: z.string().min(1, "Start date is required"),
  end_date: z.string().optional(),
  monthly_rent: z.coerce.number().min(0),
  currency: z.string().min(1),
  deposit: z.coerce.number().optional(),
})

type AssignFormValues = z.infer<typeof assignSchema>

function AssignTenantSheet({
  open,
  onOpenChange,
  propertyId,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  propertyId: string
}) {
  const createLease = useCreateLease()
  const { data: tenants = [] } = useTenants()

  const form = useForm<AssignFormValues>({
    resolver: zodResolver(assignSchema),
    defaultValues: {
      tenant_id: "",
      start_date: "",
      end_date: "",
      monthly_rent: 0,
      currency: "EUR",
      deposit: undefined,
    },
  })

  function onSubmit(values: AssignFormValues) {
    const payload = Object.fromEntries(
      Object.entries({ ...values, property_id: propertyId }).filter(
        ([, v]) => v !== "" && v !== undefined
      )
    )
    createLease.mutate(payload, {
      onSuccess: () => {
        toast.success("Tenant assigned")
        form.reset()
        onOpenChange(false)
      },
      onError: (err: unknown) => {
        toast.error(err instanceof Error ? err.message : "Failed to assign tenant")
      },
    })
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[85dvh] overflow-y-auto rounded-t-2xl">
        <SheetHeader className="mb-4">
          <SheetTitle>Assign Tenant</SheetTitle>
        </SheetHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 pb-8">
            <FormField
              control={form.control}
              name="tenant_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tenant *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select tenant" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {tenants.map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.full_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="grid grid-cols-2 gap-3">
              <FormField
                control={form.control}
                name="start_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Start Date *</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="end_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>End Date</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <FormField
                control={form.control}
                name="monthly_rent"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Monthly Rent *</FormLabel>
                    <FormControl>
                      <Input type="number" min={0} placeholder="0" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="currency"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Currency</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Currency" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {CURRENCIES.map((c) => (
                          <SelectItem key={c.code} value={c.code}>
                            {c.code}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="deposit"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Deposit</FormLabel>
                  <FormControl>
                    <Input type="number" min={0} placeholder="0" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button
              type="submit"
              className="w-full h-12 rounded-xl"
              disabled={createLease.isPending}
            >
              {createLease.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Assigning...
                </>
              ) : (
                "Assign Tenant"
              )}
            </Button>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  )
}

export default function PropertyDetailPage() {
  const { id } = useParams<{ id: string }>()
  const { data: property, isLoading, isError } = useProperty(id ?? "")
  const { data: leases } = useLeases(id)
  const { data: issues } = useMaintenanceIssues(id)
  const unassignLease = useUnassignLease()
  const { format } = useCurrency()
  const [assignOpen, setAssignOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [unassignLeaseId, setUnassignLeaseId] = useState<string | null>(null)

  // Auto-open edit sheet if ?edit=true is in URL
  useState(() => {
    if (typeof window !== "undefined") {
      const urlParams = new URLSearchParams(window.location.search)
      if (urlParams.get("edit") === "true") {
        setEditOpen(true)
        // Clean up URL without reloading
        window.history.replaceState({}, "", window.location.pathname)
      }
    }
  })

  useEffect(() => {
    if (!property) return
    const prev = document.title
    document.title = `${property.name} | ${APP_NAME}`
    return () => { document.title = prev }
  }, [property?.name])

  if (isLoading) return <ListSkeleton count={4} />

  if (isError || !property) {
    return (
      <div className="flex flex-col h-full">
        <PageHeader title="Property" back />
        <div className="flex-1 flex items-center justify-center">
          <p className="text-sm text-muted-foreground">Property not found.</p>
        </div>
      </div>
    )
  }

  const photos = property.photos ?? []
  const activeLeases = (leases ?? []).filter(
    (l) => (l as unknown as { status?: string }).status === "active"
  )

  return (
    <div className="flex flex-col h-full">
      <PageHeader title={property.name} back />

      <div className="flex-1 overflow-y-auto pb-nav bg-background relative">
        {/* Photo gallery */}
        {photos.length > 0 ? (
          <PhotoGallery photos={photos} />
        ) : (
          <div className="w-full h-[35vh] bg-primary/5 flex items-center justify-center relative mb-6">
            <Building2 className="h-16 w-16 text-primary/20" />
            <div className="absolute inset-0 bg-gradient-to-t from-background via-background/20 to-transparent pointer-events-none" />
          </div>
        )}

        {/* Content Panels below hero header */}
        <div className="px-4 -mt-14 relative z-20 space-y-5">
          {/* Header card */}
          <Card className="rounded-3xl border-border/50 bg-card/60 backdrop-blur-xl shadow-soft">
            <CardContent className="p-5">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="font-bold text-xl tracking-tight">{property.name}</h2>
                  {property.type && (
                    <p className="text-xs font-semibold text-primary uppercase tracking-widest mt-1">
                      {property.type.replace(/_/g, " ")}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge status={property.status} />
                  <Button variant="outline" size="sm" className="h-8 rounded-xl gap-1 text-xs" onClick={() => setEditOpen(true)}>
                    <Edit className="h-3.5 w-3.5" />
                    Edit
                  </Button>
                </div>
              </div>

              {(property.city || property.address_line1) && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4 font-medium">
                  <MapPin className="h-4 w-4 shrink-0 text-primary/60" />
                  <span>
                    {[property.address_line1, property.city, property.postcode]
                      .filter(Boolean)
                      .join(", ")}
                  </span>
                </div>
              )}

              <div className="flex gap-4 text-sm font-medium">
                {property.bedrooms != null && (
                  <span className="flex items-center gap-1.5 text-muted-foreground">
                    <BedDouble className="h-4 w-4 text-primary/60" />
                    {property.bedrooms} bed
                  </span>
                )}
                {property.bathrooms != null && (
                  <span className="flex items-center gap-1.5 text-muted-foreground">
                    <Bath className="h-4 w-4 text-primary/60" />
                    {property.bathrooms} bath
                  </span>
                )}
                {property.size_m2 != null && (
                  <span className="text-muted-foreground bg-primary/5 px-2 py-0.5 rounded-md text-xs">{property.size_m2} m²</span>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Current tenants quick view */}
          {activeLeases.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                Current Tenants
              </p>
              <div className="space-y-3">
                {activeLeases.map((lease) => {
                  const l = lease as unknown as { status?: string; tenant?: { id?: string; full_name?: string; photo_url?: string }; start_date?: string; end_date?: string; monthly_rent?: number }
                  return (
                    <Link key={lease.id} to={`/tenants/${l.tenant?.id}`} className="block group">
                      <Card className="rounded-2xl border-border/50 bg-card/40 backdrop-blur-md shadow-sm hover:shadow-soft hover:-translate-y-0.5 transition-all">
                        <CardContent className="p-4 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            {l.tenant?.photo_url ? (
                              <img
                                src={l.tenant.photo_url}
                                alt={l.tenant.full_name ?? ""}
                                className="w-10 h-10 rounded-full object-cover border border-border/50"
                              />
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                                <span className="text-primary font-bold text-sm">
                                  {(l.tenant?.full_name ?? "U")[0].toUpperCase()}
                                </span>
                              </div>
                            )}
                            <div>
                              <p className="font-semibold text-base mb-0.5 tracking-tight group-hover:text-primary transition-colors">
                                {l.tenant?.full_name ?? "Unknown tenant"}
                              </p>
                              <p className="text-xs text-muted-foreground font-medium">
                                {formatDate(l.start_date)} — {formatDate(l.end_date)}
                              </p>
                            </div>
                          </div>
                          <div className="text-right flex flex-col items-end gap-1.5 z-10">
                            <span className="text-sm font-bold text-foreground bg-primary/5 px-2.5 py-1 rounded-lg">
                              {format(l.monthly_rent!)}<span className="text-muted-foreground font-normal text-xs">/mo</span>
                            </span>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 text-xs text-destructive hover:text-destructive hover:bg-destructive/10 -mr-2"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setUnassignLeaseId(lease.id);
                              }}
                              disabled={unassignLease.isPending}
                            >
                              {unassignLease.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <UserMinus className="h-3 w-3 mr-1" />}
                              Unassign
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  )
                })}
              </div>
            </div>
          )}

          {/* Tabs */}
          <div>
            <Tabs defaultValue="leases" className="w-full">
              <TabsList className="w-full h-12 rounded-2xl bg-muted/40 p-1 mb-5 backdrop-blur-md">
                <TabsTrigger value="leases" className="flex-1 rounded-xl text-sm font-medium data-[state=active]:shadow-soft">
                  Leases
                </TabsTrigger>
                <TabsTrigger value="maintenance" className="flex-1 rounded-xl text-sm font-medium data-[state=active]:shadow-soft">
                  Maintenance
                </TabsTrigger>
                <TabsTrigger value="details" className="flex-1 rounded-xl text-sm font-medium data-[state=active]:shadow-soft">
                  Details
                </TabsTrigger>
              </TabsList>

              <TabsContent value="leases" className="animate-in fade-in-50 duration-300 outline-none">
                <div className="flex justify-end mb-4">
                  <Button
                    size="sm"
                    className="h-9 rounded-xl gap-1.5 shadow-glow"
                    onClick={() => setAssignOpen(true)}
                  >
                    <UserPlus className="h-4 w-4" />
                    Assign Tenant
                  </Button>
                </div>
                {!leases?.length ? (
                  <div className="py-12 bg-card/30 backdrop-blur-md rounded-3xl border border-border/50 text-center">
                    <p className="text-sm text-muted-foreground">No leases for this property.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {leases.map((lease) => {
                      const l = lease as unknown as {
                        tenant?: { id?: string; full_name?: string }
                        status?: string
                        start_date?: string
                        end_date?: string
                        monthly_rent?: number
                      }
                      return (
                        <Card key={lease.id} className="rounded-3xl border-border/50 bg-card/60 backdrop-blur-md shadow-sm">
                          <CardContent className="p-5">
                            <div className="flex justify-between items-start">
                              <div>
                                {l.tenant?.id ? (
                                  <Link
                                    to={`/tenants/${l.tenant.id}`}
                                    className="font-semibold text-base tracking-tight hover:text-primary transition-colors"
                                  >
                                    {l.tenant.full_name ?? "Unknown"}
                                  </Link>
                                ) : (
                                  <p className="font-semibold text-base tracking-tight">Unknown</p>
                                )}
                                <p className="text-xs font-medium text-muted-foreground mt-1">
                                  {formatDate(l.start_date)} — {formatDate(l.end_date)}
                                </p>
                              </div>
                              <div className="text-right flex flex-col items-end gap-1.5">
                                <StatusBadge status={l.status ?? "active"} />
                                {l.monthly_rent != null && (
                                  <p className="text-sm font-bold bg-primary/5 px-2 py-0.5 rounded-md">
                                    {format(l.monthly_rent)}<span className="text-muted-foreground font-normal text-xs">/mo</span>
                                  </p>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      )
                    })}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="maintenance" className="animate-in fade-in-50 duration-300 outline-none">
                {!issues?.length ? (
                  <div className="py-12 bg-card/30 backdrop-blur-md rounded-3xl border border-border/50 text-center">
                    <p className="text-sm text-muted-foreground">No maintenance issues.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {issues.map((issue) => (
                      <Card key={issue.id} className="rounded-3xl border-border/50 bg-card/60 backdrop-blur-md shadow-sm">
                        <CardContent className="p-5">
                          <div className="flex justify-between items-start">
                            <p className="font-semibold text-sm tracking-tight">{issue.title}</p>
                            <div className="flex gap-2">
                              <StatusBadge status={issue.priority} />
                              <StatusBadge status={issue.status} />
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="details" className="animate-in fade-in-50 duration-300 outline-none">
                <Card className="rounded-3xl border-border/50 bg-card/60 backdrop-blur-md shadow-soft">
                  <CardContent className="p-6 space-y-4">
                    {[
                      { label: "Currency", value: property.currency },
                      { label: "Notes", value: property.notes },
                    ]
                      .filter((r) => r.value)
                      .map((row) => (
                        <div key={row.label} className="flex justify-between text-sm items-center">
                          <span className="text-muted-foreground font-medium uppercase tracking-wider text-xs">{row.label}</span>
                          <span className="font-semibold text-foreground">{row.value}</span>
                        </div>
                      ))}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>

        {id && (
          <AssignTenantSheet
            open={assignOpen}
            onOpenChange={setAssignOpen}
            propertyId={id}
          />
        )}

        {property && (
          <PropertyForm
            open={editOpen}
            onOpenChange={setEditOpen}
            onSuccess={() => setEditOpen(false)}
            property={property}
          />
        )}
        <ConfirmDialog
          open={!!unassignLeaseId}
          onOpenChange={(open) => {
            if (!open) setUnassignLeaseId(null)
          }}
          title="Unassign Tenant"
          description="Are you sure you want to unassign this tenant from the property? The lease will be terminated starting today."
          confirmLabel="Unassign"
          onConfirm={() => {
            if (unassignLeaseId) {
              unassignLease.mutate(unassignLeaseId, {
                onSuccess: () => {
                  toast.success("Tenant unassigned")
                  setUnassignLeaseId(null)
                },
                onError: (err: any) => {
                  toast.error(err.response?.data?.error || "Failed to unassign")
                  setUnassignLeaseId(null)
                }
              })
            }
          }}
        />
      </div>
    </div>
  )
}
